import TokenRepo from "./token.repo";
import type { FactoryIntegration, FactoryIntegrationCatalog, FactoryIntegrationUpdate, FactoryIntegrationValidation, FactoryRuntimeSettings } from "../models/factory-integration-settings.model";

export class FactorySettingsError extends Error {
  constructor(public readonly status: number, message: string) { super(message); this.name = "FactorySettingsError"; }
}

/** Uses an isolated transport: secret requests must not be logged or retried under a different login. */
export class FactoryIntegrationSettingsRepo {
  constructor(private readonly tokens = new TokenRepo(), private readonly transport: typeof fetch = (...args) => fetch(...args)) {}

  private async request<T>(path: string, body?: FactoryIntegrationUpdate, sessionToken?: string): Promise<T> {
    const token = sessionToken ?? this.tokens.getToken();
    if (!token) throw new FactorySettingsError(401, "Phiên đăng nhập đã kết thúc.");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await this.transport(`${process.env.REACT_APP_API_URL || ""}/api/FactoryIntegrationSettings${path}?v=1.0`, {
        method: body ? "POST" : "GET", signal: controller.signal, credentials: "omit", cache: "no-store",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json", ...(process.env.REACT_APP_API_NGROK === "true" ? { "ngrok-skip-browser-warning": "true" } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data || data.code !== "00" || data.result == null) {
        // Fixed messages avoid accidentally displaying provider credentials returned by a proxy.
        const message = response.status === 409 ? "Cấu hình đã thay đổi. Tải lại cấu hình trước khi tiếp tục."
          : response.status === 403 ? "Bạn không có quyền quản trị cấu hình nhà máy này."
          : response.status === 400 ? "Cấu hình chưa hợp lệ. Kiểm tra các trường bắt buộc; đổi tài khoản hoặc bật tích hợp cần thông tin xác thực riêng."
          : response.status === 404 ? "Máy chủ chưa hỗ trợ cấu hình này."
          : "Không tải hoặc cập nhật được cấu hình. Vui lòng thử lại.";
        throw new FactorySettingsError(response.status, message);
      }
      return data.result as T;
    } catch (error) {
      if (error instanceof FactorySettingsError) throw error;
      throw new FactorySettingsError(0, "Không kết nối được máy chủ. Tải lại cấu hình để kiểm tra kết quả.");
    } finally { clearTimeout(timeout); }
  }

  getCatalog() { return this.request<FactoryIntegrationCatalog>("/catalog"); }
  getRuntime(token?: string) { return this.request<FactoryRuntimeSettings>("/runtime", undefined, token); }
  validate(id: string, input: FactoryIntegrationUpdate) { return this.request<FactoryIntegrationValidation>(`/${encodeURIComponent(id)}/validate`, input); }
  save(id: string, input: FactoryIntegrationUpdate) { return this.request<FactoryIntegration>(`/${encodeURIComponent(id)}`, input); }
}

export const factoryIntegrationSettingsRepo = new FactoryIntegrationSettingsRepo();
